import { CheckIcon } from "@heroicons/react/24/outline";
import { ASSET_CLASS_OPTIONS } from "../profileMapping";
import PortfolioQuickAdd from "./PortfolioQuickAdd";
import { CARD, CARD_ACTIVE, HINT, INPUT, SECTION_LABEL } from "../ui/theme";
import type { AssetClassId, OnboardingAnswers } from "../types";

interface StepProfileProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
}

/**
 * Step 1: what this trader cares about. Asset-class cards are pre-checked
 * from squawk channel roles; the free text is the concierge's input and is
 * kept verbatim.
 */
export default function StepProfile({ answers, onChange }: StepProfileProps) {
  const toggleAssetClass = (id: AssetClassId) => {
    const current = answers.assetClasses;
    onChange({
      ...answers,
      assetClasses: current.includes(id)
        ? current.filter((a) => a !== id)
        : [...current, id],
    });
  };

  return (
    <div className="space-y-7">
      {/* First, and given the most room: it is the highest-value field on the
          screen and the only one that can produce a whole setup, yet it sat
          below the cards where it read as an afterthought. */}
      <div>
        <div className="flex items-center gap-2">
          <label htmlFor="nq-onb-freetext" className={SECTION_LABEL}>
            In your words
          </label>
          <span className="rounded border border-squawk-blue/30 bg-squawk-blue/10 px-1.5 py-0.5 text-2xs font-medium uppercase tracking-wide text-squawk-blue">
            Fastest
          </span>
        </div>
        {/* Above the field, matching the block below it. It was underneath,
            which made two adjacent sections read as different kinds of thing
            when they are the same shape. */}
        <p className={`${HINT} mt-0.5`}>
          Write it however you&rsquo;d say it out loud — we read plain English
          and build the watchlists, filters and dashboard to match.
        </p>
        <div className="group relative mt-2">
          <textarea
            id="nq-onb-freetext"
            value={answers.freeText ?? ""}
            onChange={(e) => onChange({ ...answers, freeText: e.target.value })}
            placeholder="e.g. US tech earnings, FOMC speakers, and anything moving crude"
            rows={3}
            maxLength={2000}
            className={`${INPUT} resize-none leading-relaxed`}
          />
        </div>
      </div>

      <PortfolioQuickAdd
        tickers={answers.tickers ?? []}
        onChange={(tickers) => onChange({ ...answers, tickers })}
      />

      <div>
        <span className={SECTION_LABEL}>Or just pick your markets</span>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {ASSET_CLASS_OPTIONS.map((option, index) => {
          const selected = answers.assetClasses.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => toggleAssetClass(option.id)}
              aria-pressed={selected}
              style={{ ["--nq-i" as string]: index }}
              className={`nq-onb-rise relative cursor-pointer px-3.5 py-3 text-left transition-colors ${
                selected ? CARD_ACTIVE : `${CARD} hover:border-gray-600`
              }`}
            >
              {selected && (
                <CheckIcon
                  className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-squawk-blue"
                  strokeWidth={3}
                />
              )}
              <div
                className={`text-sm font-semibold ${
                  selected ? "text-squawk-blue" : "text-gray-100"
                }`}
              >
                {option.label}
              </div>
              <div className="mt-0.5 text-xs leading-snug text-gray-400">
                {option.blurb}
              </div>
            </button>
          );
        })}
        </div>
      </div>

    </div>
  );
}
